import { faArrowLeft, faArrowRight, faTimes } from '@fortawesome/free-solid-svg-icons'
import React, { useEffect, useState, useRef } from 'react'
import Popup from 'reactjs-popup'
import { Flex, Image, ImageProps, Box, Text } from 'rebass'
import _ from 'lodash'

import ButtonFa from './ButtonFa'
import { theme } from '../theme'
import { buildCloudinaryUrl } from '../services/cloudinary'

interface CarouselFooterProps {
    currentIndex: number
    maxIndex: number
    onClickLeft?: () => void
    onClickRight?: () => void
}

export const CarouselFooter: React.FC<CarouselFooterProps> = ({
    currentIndex,
    maxIndex,
    onClickLeft,
    onClickRight,
}) => {
    return (
        <Flex pt={theme.spacing.xxxxs} justifyContent="center" alignItems="center">
            {onClickLeft && (
                <ButtonFa icon={faArrowLeft} iconSize="1x" onClick={onClickLeft} disabled={currentIndex <= 0} />
            )}
            <Text px={theme.spacing.xxs} fontSize="0.8em" textAlign="center">{`${currentIndex + 1} / ${
                maxIndex + 1
            }`}</Text>
            {onClickRight && (
                <ButtonFa
                    icon={faArrowRight}
                    iconSize="1x"
                    onClick={onClickRight}
                    disabled={currentIndex >= maxIndex}
                />
            )}
        </Flex>
    )
}

export interface MediaData {
    image: string
    img_main: string
    resource_type: string
    public_id: string
    uuid: string
}

interface MediaDataWithUrl extends MediaData {
    url?: string
}

interface MediaViewerProps extends ImageProps {
    key?: string
    mediaData: MediaData | MediaData[]
    showMediaIndex?: number | undefined
    maxHeight?: string | undefined
    showPopup?: boolean
    showArrowButtons?: boolean
    popupOnly?: boolean
    isOpenModal?: boolean
    setModalOpen?: any
    setMediaData?: any
    setSelectedMediaIndex?: any
    style?: { [key: string]: string }
    showCarouselFooter?: boolean
}

interface PopupContentProps {
    contentData: MediaDataWithUrl[]
    selectedIndex: number
    close: () => void
}

const PopupContent: React.FC<PopupContentProps> = ({ contentData, selectedIndex, close, ...mediaProps }) => {
    return (
        <>
            <Box style={{ position: 'absolute', right: 0, top: 0, zIndex: 1 }}>
                <ButtonFa icon={faTimes} iconSize="1x" onClick={close} />
            </Box>
            <Box p={theme.spacing.s} sx={{ textAlign: 'center' }} maxHeight="100vh">
                {contentData[selectedIndex].resource_type === 'image' && (
                    <Image
                        src={contentData[selectedIndex].url}
                        style={{ maxHeight: '75vh', maxWidth: '95%', objectFit: 'contain' }}
                        {...mediaProps}
                    />
                )}
                {contentData[selectedIndex].resource_type === 'video' && (
                    <video
                        key={selectedIndex}
                        controls
                        style={{ maxHeight: '75vh', maxWidth: '95%', objectFit: 'contain' }}
                    >
                        <source src={contentData[selectedIndex].url} />
                    </video>
                )}
            </Box>
        </>
    )
}

const MediaViewer: React.FC<MediaViewerProps> = ({
    mediaData,
    showMediaIndex = undefined,
    maxHeight = undefined,
    showPopup = true,
    showArrowButtons = true,
    popupOnly = false, // if want to display popup in parent component and use redux to control it, please set it to true
    isOpenModal = false, // only be used if popupOnly = true
    setModalOpen = undefined, // only be used for control the popupOnly MediaViwer
    setMediaData = undefined, // only be used for control the popupOnly MediaViwer
    setSelectedMediaIndex = undefined, // only be used for control the popupOnly MediaViwer
    style = undefined,
    showCarouselFooter = true, // set to false if used in app preview
    ...mediaProps
}) => {
    let contentData: MediaDataWithUrl[]
    const videoRef = useRef<HTMLVideoElement | null>(null)
    if (_.isArray(mediaData)) {
        contentData = mediaData
    } else {
        contentData = [mediaData]
    }
    const totalContent = contentData.length
    const needsCarousel = totalContent > 1 && showCarouselFooter
    const maxIndex = totalContent - 1

    const [selectedIndex, setSelectedIndex] = useState<number>(showMediaIndex || 0)

    const goNext = () => {
        if (selectedIndex >= maxIndex) return
        setSelectedIndex(selectedIndex >= maxIndex ? 0 : selectedIndex + 1)
    }
    const goBack = () => {
        if (selectedIndex <= 0) return
        setSelectedIndex(selectedIndex === 0 ? maxIndex : selectedIndex - 1)
    }
    const mediaSelection = (data: MediaDataWithUrl) => {
        if (data.resource_type === 'video') {
            return (
                <video
                    ref={el => (videoRef.current = el)}
                    key={data.public_id}
                    src={data.url}
                    controls
                    height={maxHeight || '100%'}
                    style={style ? style : { maxWidth: '100%', objectFit: 'contain' }}
                    muted={false}
                    onClick={(e: React.MouseEvent<HTMLVideoElement, MouseEvent>) => {
                        if (setMediaData && setModalOpen && setSelectedMediaIndex) {
                            e.preventDefault()
                            videoRef.current?.pause()
                            setMediaData(contentData)
                            setSelectedMediaIndex(selectedIndex)
                            setModalOpen(true)
                        }
                    }}
                />
            )
        }
        return (
            <Image
                src={data.url}
                height={maxHeight || '100%'}
                sx={style ? style : { maxWidth: '100%', objectFit: 'contain' }}
                onClick={() => {
                    if (setMediaData && setModalOpen && setSelectedMediaIndex) {
                        setMediaData(contentData)
                        setSelectedMediaIndex(selectedIndex)
                        setModalOpen(true)
                    }
                }}
            />
        )
    }

    useEffect(() => {
        if (showMediaIndex !== undefined) {
            setSelectedIndex(showMediaIndex)
        }
    }, [showMediaIndex])

    contentData = contentData.map(data => ({
        ...data,
        url: buildCloudinaryUrl(data),
    }))

    if (!contentData || contentData.length === 0) return null

    if (popupOnly) {
        return (
            <>
                <Box>
                    <Popup
                        modal
                        open={isOpenModal}
                        closeOnDocumentClick
                        onOpen={() => setModalOpen(true)}
                        onClose={() => setModalOpen(false)}
                        contentStyle={{ maxWidth: '90%', width: 'auto' }}
                        overlayStyle={{ zIndex: 1001 }} // set z-index to 1001 is to avoid z fighting with edit request history modal
                    >
                        {close => (
                            <>
                                <PopupContent
                                    contentData={contentData}
                                    selectedIndex={selectedIndex}
                                    close={close}
                                    {...mediaProps}
                                />
                                {needsCarousel && (
                                    <CarouselFooter
                                        currentIndex={selectedIndex}
                                        maxIndex={maxIndex}
                                        onClickLeft={showArrowButtons ? goBack : undefined}
                                        onClickRight={showArrowButtons ? goNext : undefined}
                                    />
                                )}
                            </>
                        )}
                    </Popup>
                </Box>
            </>
        )
    } else {
        if (!showPopup) {
            return (
                <Flex flexDirection="column">
                    <Box
                        height={maxHeight || '75vh'}
                        sx={{
                            maxHeight: maxHeight || '75vh',
                            textAlign: 'center',
                        }}
                    >
                        {mediaSelection(contentData[selectedIndex])}
                    </Box>
                    {needsCarousel && (
                        <CarouselFooter
                            currentIndex={selectedIndex}
                            maxIndex={maxIndex}
                            onClickLeft={showArrowButtons ? goBack : undefined}
                            onClickRight={showArrowButtons ? goNext : undefined}
                        />
                    )}
                </Flex>
            )
        }
        return (
            <>
                <Box
                    height={maxHeight || '500px'}
                    sx={{
                        maxHeight: maxHeight || '500px',
                        textAlign: 'center',
                    }}
                >
                    <Popup
                        modal
                        trigger={mediaSelection(contentData[selectedIndex])}
                        contentStyle={{ maxWidth: '90%', width: 'auto' }}
                    >
                        {close => (
                            <PopupContent
                                contentData={contentData}
                                selectedIndex={selectedIndex}
                                close={close}
                                {...mediaProps}
                            />
                        )}
                    </Popup>
                </Box>
                {needsCarousel && (
                    <CarouselFooter
                        currentIndex={selectedIndex}
                        maxIndex={maxIndex}
                        onClickLeft={showArrowButtons ? goBack : undefined}
                        onClickRight={showArrowButtons ? goNext : undefined}
                    />
                )}
            </>
        )
    }
}

export default MediaViewer
